import { CrossIcon } from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import { Button } from "../../../components/ui/button";
import { FormActions } from "../../../components/ui/form-actions";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../../components/ui/select";

import {
    type PolicyConfigDemo,
    PolicyConfiguration
} from "./PolicyConfiguration";
import {
    initialValueFor,
    isPolicyValueComplete,
    newUserDescription,
    walletDescription
} from "./policyFixtures";
import type { ConfigurablePolicy, Policy } from "./types";

/** The controls a scripted sim drives inside the create-policy editor, in
 *  playthrough order. */
export type PolicyCreatorControl =
    | "type-select"
    | "config-value"
    | "joined-within"
    | "tranche-amount"
    | "tranche-mult"
    | "mode-toggle"
    | "tranche-add"
    | "add-policy";

/** Forced-interaction phase the cursor is painting on the active control. */
export type PolicyCreatorDemoPhase = "hover" | "press";

/**
 * Sim-only demo seam for the cursor-driven Policies sim (see
 * {@link ProgramPoliciesSim}). When set, the named `control` paints its forced
 * hover/focus VISUAL so a scripted cursor hovering/clicking it is believable.
 * Production callers omit it → the editor renders exactly as before. Every
 * control also carries a stable `data-sim-cursor-key` so the cursor can target
 * it; those attributes are inert in production.
 */
export interface PolicyCreatorDemo {
    control: PolicyCreatorControl | null;
    phase: PolicyCreatorDemoPhase;
    /** Sim-only — force the "Policy type" Select dropdown OPEN (so the scripted
     *  "click" actually reveals the options). Only meaningful for the
     *  `type-select` control. Inert in production. */
    open?: boolean;
    /** Sim-only — portal the OPEN "Policy type" dropdown into this element (the
     *  sim's cursor container) so its options live where the scripted pointer
     *  is anchored and can be reached. Omit in production. */
    portalContainer?: HTMLElement | null;
    /** Sim-only — the policy-type option ID the scripted cursor is currently
     *  over; that `SelectItem` paints its highlighted visual. Omit in
     *  production. */
    highlightType?: string | null;
    /** Sim-only — for the `mode-toggle` control (Specific Access Control's
     *  Whitelist / Blacklist / KYC Only 3-way): the mode value the cursor is
     *  currently on. That ToggleMenuItem paints the forced hover/press. */
    modeTarget?: string | null;
    /** Sim-only — for the `tranche-amount` / `tranche-mult` controls: the row
     *  index the cursor is currently on. That row's amount/multiplier field
     *  paints the forced hover/press. */
    trancheRow?: number | null;
}

export interface PolicyCreatorProps {
    /** Available catalog policy templates the program doesn't already have —
     *  the "Policy type" dropdown picks from these, and the preset variant
     *  looks its type up here. */
    available: ConfigurablePolicy[];
    /** Attach the configured policy to the program (closes the editor). */
    onAdd: (policy: Policy, value: number | string) => void;
    /** Close the editor without adding. */
    onCancel: () => void;
    /** Pre-selected catalog policy id — drives the type-specific config
     *  without the user picking from the dropdown (the Suggested-card variant). */
    presetType?: string;
    /** Hide the "Policy type" dropdown — the type is already chosen. */
    hideTypeSelector?: boolean;
    /** Sim-only — drives forced hover/press on one editor control at a time.
     *  Omit in production. */
    demo?: PolicyCreatorDemo;
    /** Sim-only — lift the chosen type + in-progress value out of the editor
     *  so a scripted sim can drive them with no DOM clicks. When set (non-
     *  null), the editor renders CONTROLLED from these instead of its internal
     *  state. Production omits both → internal state as before. */
    controlledPolicyId?: string | null;
    controlledValue?: number | string;
    onControlledValueChange?: (next: number | string) => void;
}

/**
 * PolicyCreator — the inline create-policy editor (draft programs only). A
 * Domain Component: a compact raised card with a title + 'x' close + a
 * policy-type dropdown, and, once a type is chosen, the type-specific
 * configuration for that policy, then "Add Policy" / "Cancel".
 *
 * One component covers both editor variants the Policies section opens:
 * - **generic** (default) — opened by "+Create Policy"; shows the dropdown +
 *   a "Create Policy" title.
 * - **preset** — opened by a Suggested card; pass `presetType` +
 *   `hideTypeSelector` so the type is fixed and the title reads "Create [Type]
 *   Policy". Give the element a `key={presetType}` at the call site.
 */
export function PolicyCreator({
    available,
    onAdd,
    onCancel,
    presetType,
    hideTypeSelector = false,
    demo,
    controlledPolicyId,
    controlledValue,
    onControlledValueChange
}: PolicyCreatorProps): React.ReactElement {
    // Controlled (sim) mode: the driver lifts the chosen type + value so the
    // sequence can script them with no DOM clicks. `controlledPolicyId`
    // undefined ⇒ internal (production) state.
    const controlled = controlledPolicyId !== undefined;

    const [internalPolicyId, setInternalPolicyId] = useState<string | null>(
        presetType ?? null
    );
    const [internalValue, setInternalValue] = useState<number | string>(() =>
        presetType ? initialValueFor(available, presetType) : ""
    );

    const policyId = controlled ? controlledPolicyId : internalPolicyId;
    const value = controlled ? (controlledValue ?? "") : internalValue;
    const setValue = (next: number | string): void => {
        if (controlled) onControlledValueChange?.(next);
        else setInternalValue(next);
    };

    // Sim demo helpers. `demoOn(id)` → the control the cursor is on. Button-
    // like controls (Add Policy, +Add Tranche) map hover→forceHover /
    // press→forcePressed; field-like controls (type Select, config value)
    // map hover→forceHover / press→forceFocus. All no-ops in production.
    const demoOn = (id: PolicyCreatorControl): boolean => demo?.control === id;
    const configDemo: PolicyConfigDemo = {
        valuePhase: demoOn("config-value") ? (demo?.phase ?? null) : null,
        joinedWithinPhase: demoOn("joined-within")
            ? (demo?.phase ?? null)
            : null,
        trancheAmountRow: demoOn("tranche-amount")
            ? (demo?.trancheRow ?? null)
            : null,
        trancheAmountPhase: demoOn("tranche-amount")
            ? (demo?.phase ?? null)
            : null,
        trancheMultiplierRow: demoOn("tranche-mult")
            ? (demo?.trancheRow ?? null)
            : null,
        trancheMultiplierPhase: demoOn("tranche-mult")
            ? (demo?.phase ?? null)
            : null,
        trancheAddPhase: demoOn("tranche-add") ? (demo?.phase ?? null) : null,
        modePhase: demoOn("mode-toggle") ? (demo?.phase ?? null) : null,
        modeTarget: demoOn("mode-toggle") ? (demo?.modeTarget ?? null) : null
    };

    const policy = available.find((p) => p.id === policyId) ?? null;

    // Title reads "Create [Type] Policy" when preset; "Create Policy"
    // otherwise.
    const presetPolicy = presetType
        ? (available.find((p) => p.id === presetType) ?? null)
        : null;
    const heading = presetPolicy
        ? `Create ${presetPolicy.title} Policy`
        : "Create Policy";

    function chooseType(id: string | null): void {
        const next = available.find((p) => p.id === id);
        if (!next) return;
        // In controlled (sim) mode the driver owns the type; only the value
        // resets here for the internal (production) path.
        if (!controlled) setInternalPolicyId(next.id);
        setValue(initialValueFor(available, next.id));
    }

    const description = policy
        ? policy.kind === "slider"
            ? policy.describe(Number(value))
            : policy.kind === "wallet"
              ? walletDescription(policy, value)
              : policy.kind === "new-user"
                ? newUserDescription(policy, value)
                : policy.description
        : null;

    return (
        <div className="relative bg-surface-raised px-4 py-5">
            <Button
                variant="transparent"
                interaction="default"
                size="fit"
                onClick={onCancel}
                aria-label="Close"
                // Pixel-match the previous bare CloseEditorButton: absolute
                // top-right, neutral icon that darkens to foreground-base on
                // hover. The `transparent` variant's own hover/active curve is
                // ember, so override hover + active to the base foreground.
                className="absolute right-3 top-3 text-foreground-neutral hover:text-foreground-base active:text-foreground-base"
            >
                <CrossIcon className="size-4" />
            </Button>
            <Text variant="h3" className="text-foreground-base mb-4">
                {heading}
            </Text>
            {!hideTypeSelector && (
                <div className="max-w-md">
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-muted block mb-2"
                    >
                        Policy type
                    </Text>
                    <Select
                        value={policyId ?? ""}
                        onValueChange={(v) => chooseType(v)}
                        // Sim-only controlled-open seam: in demo mode the
                        // scripted cursor forces the dropdown open so the
                        // options actually show. Undefined in production →
                        // Base UI stays uncontrolled (opens on real click).
                        open={
                            demo
                                ? demoOn("type-select") && demo.open === true
                                : undefined
                        }
                        onOpenChange={demo ? (): void => {} : undefined}
                    >
                        <SelectTrigger
                            className="w-full"
                            data-sim-cursor-key="pol-type-select"
                            forceHover={
                                demoOn("type-select") && demo?.phase === "hover"
                            }
                            forceFocus={
                                demoOn("type-select") && demo?.phase === "press"
                            }
                        >
                            <SelectValue placeholder="Select a policy type">
                                {() =>
                                    policy
                                        ? policy.title
                                        : "Select a policy type"
                                }
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent
                            align="start"
                            alignItemWithTrigger={false}
                            // Sim-only — portal INTO the cursor container so the
                            // option list is reachable by the scripted pointer.
                            // Undefined in production → `<body>`.
                            container={demo?.portalContainer ?? undefined}
                        >
                            {available.map((p) => (
                                <SelectItem
                                    key={p.id}
                                    value={p.id}
                                    // Sim-only — a stable per-option target for
                                    // the cursor + the forced highlighted visual
                                    // when it's over this option. Inert in prod.
                                    data-sim-cursor-key={
                                        demo
                                            ? `pol-type-opt-${p.id}`
                                            : undefined
                                    }
                                    forceHighlighted={
                                        demo?.highlightType === p.id
                                    }
                                    // Sim: only the scripted forced highlight
                                    // paints — suppress Base UI's selected-item-
                                    // on-open auto-highlight so exactly one
                                    // option (under the cursor) lights up.
                                    suppressAutoHighlight={!!demo}
                                >
                                    {p.title}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            {/* Expanded config — appears in place once a type is chosen. */}
            {policy && (
                <div className="mt-4">
                    <Text variant="small" className="text-foreground-muted">
                        {description}
                    </Text>
                    <div className="mt-3 max-w-md">
                        <PolicyConfiguration
                            policy={policy}
                            value={value}
                            onChange={setValue}
                            demo={configDemo}
                            valueCursorKey="pol-config-value"
                            joinedWithinCursorKey="pol-joined-within"
                            trancheRowCursorKeyPrefix="pol-tranche"
                            trancheAddCursorKey="pol-tranche-add"
                            modeCursorKeyPrefix="pol-mode"
                        />
                    </div>
                    <FormActions
                        className="mt-5"
                        size="sm"
                        primaryLabel="Add Policy"
                        // Gated until the chosen type's required fillable
                        // fields are all filled — config fields now start
                        // empty, so the editor can't commit an unfilled policy.
                        primaryDisabled={!isPolicyValueComplete(policy, value)}
                        onPrimary={() => onAdd(policy, value)}
                        cancelLabel="Cancel"
                        onCancel={onCancel}
                        primaryCursorKey="pol-add-policy"
                        primaryForceHover={
                            demoOn("add-policy") && demo?.phase === "hover"
                        }
                        primaryForcePressed={
                            demoOn("add-policy") && demo?.phase === "press"
                        }
                    />
                </div>
            )}
        </div>
    );
}
